import React, { useState, useEffect } from 'react';
import { getPageBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const data = await getPageBySlug('om-os');
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Om os · Vissenbjerg Bageri',
    description: pageData?.meta_description || 'Læs om Vissenbjerg Bageri og vores dygtige og kreative medarbejdere. Vi værner om de gamle håndværkstraditioner.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Vores Historie & Værdier
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Om Vissenbjerg Bageri
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate">
              Håndværk, kvalitet og tradition siden 1925
            </h2>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vores bageri har ligget i Vestergade siden 1925, og vi er stolte af at føre de stolte danske bagerhåndværkstraditioner videre. Hver eneste dag stræber vi efter at levere endnu bedre varer og smagsoplevelser for vores kunder.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Uanset om der er tale om klassisk brødfremstilling, udvikling af nye langtidshævede surdejs- og stenovnsbrød eller detaljerede og æstetiske lagkagekreationer, går vi aldrig på kompromis med kvaliteten. Alt laves helt fra bunden med de bedste råvarer.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vi sætter en stor ære i at have kvalificerede og dygtige medarbejdere, der alle værner om de gamle håndværkstraditioner samtidig med, at de er innovative og kreative. De er hver dag med til at bevare vores gode forretning, og uden dem kunne vi ikke udvikle og forbedre vores opskrifter og bagværk.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gold-brown/10 h-[450px]">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/image.png"
              alt="Bageriet i Vestergade"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Team Section */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mt-20 mb-12">
          <h2 className="font-serif text-3xl font-bold text-chocolate">Vores Dygtige Team</h2>
          <p className="text-base text-chocolate/70">
            Mød folkene bag de sprøde skorper og de søde sager. Udover bagermester selv har vi i øjeblikket fire bagersvende, en lærling og en konditor ansat i forretningen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="rounded-xl bg-white p-6 shadow-md border border-gold-brown/10 text-center space-y-3">
            <div className="h-16 w-12 mx-auto text-gold-brown flex items-center justify-center">
              <i className="fa-solid fa-user-tie text-4xl" />
            </div>
            <h3 className="font-serif text-xl font-bold text-chocolate">Bagermesteren</h3>
            <p className="text-sm text-chocolate/70">
              Står i spidsen for bageriet og sikrer, at de gamle traditioner og den høje kvalitet altid overholdes.
            </p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-md border border-gold-brown/10 text-center space-y-3">
            <div className="h-16 w-12 mx-auto text-gold-brown flex items-center justify-center">
              <i className="fa-solid fa-wheat-awn text-4xl" />
            </div>
            <h3 className="font-serif text-xl font-bold text-chocolate">Bagersvendene</h3>
            <p className="text-sm text-chocolate/70">
              Vores fire dygtige svende og lærling, der møder ind kl. 01.30 - kl. 07.00 hver nat for at bage frisk brød til dig.
            </p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-md border border-gold-brown/10 text-center space-y-3">
            <div className="h-16 w-12 mx-auto text-gold-brown flex items-center justify-center">
              <i className="fa-solid fa-cake-candles text-4xl" />
            </div>
            <h3 className="font-serif text-xl font-bold text-chocolate">Konditoren</h3>
            <p className="text-sm text-chocolate/70">
              Vores kreative konditor med over 20 års erfaring, der skaber smukke festkager og lagkager.
            </p>
          </div>
        </div>

        {/* CMS Rich Text Content - Beautifully Redesigned & Moved above footer */}
        <div className="relative max-w-4xl mx-auto mt-24" data-dev-id="src/pages/About.jsx:70:8">
          {/* Subtle background wheat icon */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
            <i className="fa-solid fa-wheat-awn text-[15rem] md:text-[20rem] text-gold-brown"></i>
          </div>

          <div className="relative bg-white/40 backdrop-blur-md border border-gold-brown/15 rounded-3xl p-8 md:p-16 text-center shadow-xl space-y-10 overflow-hidden">
            {/* Corner Accents */}
            <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-gold-brown/20 rounded-tl-lg pointer-events-none"></div>
            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gold-brown/20 rounded-tr-lg pointer-events-none"></div>
            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gold-brown/20 rounded-bl-lg pointer-events-none"></div>
            <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-gold-brown/20 rounded-br-lg pointer-events-none"></div>

            {/* Header Accent */}
            <div className="flex flex-col items-center space-y-2">
              <span className="font-accent text-3xl md:text-4xl text-gold-brown">Vores Bagerfilosofi</span>
              <div className="flex items-center gap-3 w-full max-w-xs justify-center">
                <span className="h-[1px] bg-gold-brown/30 flex-1"></span>
                <i className="fa-solid fa-wheat-awn text-gold-brown text-sm"></i>
                <span className="h-[1px] bg-gold-brown/30 flex-1"></span>
              </div>
            </div>

            {/* First Quote Block */}
            <div className="space-y-4 max-w-2xl mx-auto relative">
              <i className="fa-solid fa-quote-left text-4xl text-gold-brown/10 absolute -top-6 -left-6 md:-left-10"></i>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate tracking-wide uppercase">
                Hvad er en lækker kage og et godt brød?
              </h2>
              <p className="font-serif text-lg md:text-xl text-chocolate/90 italic leading-relaxed">
                – Vi mener det bedste resultalt skabes i et kreativt bageri, hvor personalet hver dag inspirerer hinanden til nye spændende brød og kagetyper – selvfølgelig lavet helt fra bunden!
              </p>
            </div>

            {/* Elegant Divider */}
            <div className="flex items-center justify-center gap-2 py-1">
              <span className="w-2 h-2 rounded-full bg-gold-brown/40"></span>
              <span className="w-24 h-[1px] bg-gold-brown/30"></span>
              <span className="w-2 h-2 rounded-full bg-gold-brown/40"></span>
            </div>

            {/* Second Quote Block */}
            <div className="space-y-4 max-w-2xl mx-auto relative">
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-chocolate tracking-wide uppercase">
                Hvis vi selv skal sige det …
              </h3>
              <p className="font-accent text-3xl md:text-4xl text-gold-brown leading-relaxed">
                … Så synes vi faktisk at vi gør det godt!
              </p>
              <p className="font-serif text-lg md:text-xl text-chocolate/80 italic leading-relaxed">
                Og det kan smages i vores bagværk og vores kager!
              </p>
              <i className="fa-solid fa-quote-right text-4xl text-gold-brown/10 absolute -bottom-6 -right-6 md:-right-10"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
